import { FFmpeg } from '@ffmpeg/ffmpeg'

const CUSTOM_CORE_URL = process.env.NEXT_PUBLIC_FFMPEG_CORE_PATH?.trim()

const CUSTOM_LOAD_CONFIG = (() => {
  if (!CUSTOM_CORE_URL) {
    return undefined
  }

  const normalized = CUSTOM_CORE_URL
  const lastSlashIndex = normalized.lastIndexOf('/')

  const base = lastSlashIndex >= 0 ? normalized.slice(0, lastSlashIndex + 1) : ''
  return {
    coreURL: normalized,
    wasmURL: `${base}ffmpeg-core.wasm`,
    workerURL: `${base}ffmpeg-core.worker.js`,
  }
})()

type FFmpegInstance = InstanceType<typeof FFmpeg>

let ffmpegInstance: FFmpegInstance | null = null
let ffmpegLoadingPromise: Promise<FFmpegInstance> | null = null

async function loadFfmpeg(): Promise<FFmpegInstance> {
  if (ffmpegInstance) {
    return ffmpegInstance
  }

  if (typeof window === 'undefined') {
    throw new Error('FFmpeg is only available in the browser environment')
  }

  if (!ffmpegLoadingPromise) {
    const instance = new FFmpeg()

    ffmpegLoadingPromise = instance
      .load(CUSTOM_LOAD_CONFIG)
      .then(() => {
        ffmpegInstance = instance
        return instance
      })
      .catch((error: unknown) => {
        ffmpegLoadingPromise = null
        throw error
      })
  }

  return ffmpegLoadingPromise
}

function ensureUint8Array(data: unknown): Uint8Array {
  if (data instanceof Uint8Array) {
    return data
  }

  if (typeof data === 'string') {
    return new TextEncoder().encode(data)
  }

  throw new Error('Unexpected file data returned from FFmpeg')
}

async function writeInputFile(ffmpeg: FFmpegInstance, fileName: string, blob: Blob) {
  const buffer = await blob.arrayBuffer()
  await ffmpeg.writeFile(fileName, new Uint8Array(buffer))
}

async function readOutputFile(ffmpeg: FFmpegInstance, fileName: string) {
  const data = await ffmpeg.readFile(fileName)
  return ensureUint8Array(data)
}

function uniqueFileName(prefix: string, extension: string) {
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `${prefix}-${id}.${extension}`
}

export async function convertVideoToMp4(blob: Blob): Promise<Blob> {
  if (blob.type === 'video/mp4') {
    return blob
  }

  const ffmpeg = await loadFfmpeg()

  const inputExt = blob.type.includes('webm') ? 'webm' : 'mp4'
  const inputName = uniqueFileName('input', inputExt)
  const outputName = uniqueFileName('output', 'mp4')

  await writeInputFile(ffmpeg, inputName, blob)

  try {
    await ffmpeg.exec([
      '-i',
      inputName,
      '-c:v',
      'libx264',
      '-preset',
      'veryfast',
      '-crf',
      '23',
      '-pix_fmt',
      'yuv420p',
      '-c:a',
      'aac',
      '-b:a',
      '192k',
      '-movflags',
      'faststart',
      outputName,
    ])
  } catch (error) {
    throw new Error('Failed to convert video to MP4')
  } finally {
    await ffmpeg.deleteFile(inputName).catch(() => undefined)
  }

  const data = await readOutputFile(ffmpeg, outputName)
  await ffmpeg.deleteFile(outputName).catch(() => undefined)

  return new Blob([new Uint8Array(data)], { type: 'video/mp4' })
}

export async function convertAudioToMp3(blob: Blob): Promise<Blob> {
  if (blob.type === 'audio/mpeg' || blob.type === 'audio/mp3') {
    return blob.type === 'audio/mpeg' ? blob : blob.slice(0, blob.size, 'audio/mpeg')
  }

  const ffmpeg = await loadFfmpeg()

  const inputExt = blob.type.includes('webm') ? 'webm' : 'ogg'
  const inputName = uniqueFileName('input', inputExt)
  const outputName = uniqueFileName('output', 'mp3')

  await writeInputFile(ffmpeg, inputName, blob)

  try {
    await ffmpeg.exec([
      '-i',
      inputName,
      '-vn',
      '-ar',
      '44100',
      '-ac',
      '2',
      '-b:a',
      '192k',
      outputName,
    ])
  } catch (error) {
    throw new Error('Failed to convert audio to MP3')
  } finally {
    await ffmpeg.deleteFile(inputName).catch(() => undefined)
  }

  const data = await readOutputFile(ffmpeg, outputName)
  await ffmpeg.deleteFile(outputName).catch(() => undefined)

  return new Blob([new Uint8Array(data)], { type: 'audio/mpeg' })
}

export async function convertImageToJpeg(blob: Blob): Promise<Blob> {
  if (blob.type === 'image/jpeg') {
    return blob
  }

  const drawToCanvas = async () => {
    if (typeof createImageBitmap === 'function') {
      const imageBitmap = await createImageBitmap(blob)
      const canvas = document.createElement('canvas')
      canvas.width = imageBitmap.width
      canvas.height = imageBitmap.height
      const context = canvas.getContext('2d')

      if (!context) {
        imageBitmap.close()
        throw new Error('Failed to render image for conversion')
      }

      context.drawImage(imageBitmap, 0, 0)
      imageBitmap.close()
      return canvas
    }

    const image = await loadImage(blob)
    const canvas = document.createElement('canvas')
    canvas.width = image.width
    canvas.height = image.height
    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('Failed to render image for conversion')
    }
    context.drawImage(image, 0, 0)
    return canvas
  }

  const canvas = await drawToCanvas()

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blobResult) => {
        if (blobResult) {
          resolve(blobResult)
        } else {
          reject(new Error('Failed to export image as JPEG'))
        }
      },
      'image/jpeg',
      0.9,
    )
  })
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to decode image'))
    }
    image.src = url
  })
}
